import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Fab, Tooltip } from '@material-ui/core';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import InfiniteScrollTableView from '../../../components/InfiniteScrollTableView';
import { TimeSheetList, TimeSheetCountType } from '../../../store/modules/timeSheet/type';
import TableEmptyState from '../../../components/TableView/TableEmptyState';
import jsonpath from 'jsonpath';
import { sum, round } from 'lodash';
import moment from 'moment';
import { IColumn, Order } from '../../../store/modules/application/types';

declare module '@material-ui/core/styles/withStyles' {
    // Augment the BaseCSSProperties so that we can control jss-rtl
    interface BaseCSSProperties {
        /*
         * Used to control if the rule-set should be affected by rtl transformation
         */
        flip?: boolean;
    }
}

type TimeSheetProps = {
    timeSheetList: TimeSheetList;
    isLoading: boolean;
    skip: number;
    setSkip: Function;
    limit: number;
    getTimeSheetList: Function;
    hasMore: boolean;
    timeSheetCount: TimeSheetCountType;
    sortOrder: Order;
    sortBy: string;
    setSortOrder: Function;
    setSortColumn: Function;
    isFilteredLoading: boolean;
    onSelectTimeSheetRow: Function;
};

const componentStyles = makeStyles(() =>
    createStyles({
        root: {
            padding: '0px 40px 40px 40px',
        },
        tablePaginationInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
        },
        container: {
            display: 'flex',
            width: '100%',
            backgroundColor: 'white',
        },
        fab: {
            position: 'absolute',
            bottom: 55,
            left: 'calc( 100vw - 100px)',
            zIndex: 3,
            backgroundColor: 'white',
        },
        countSpan: {
            color: '#484848',
            fontSize: 14,
            letterSpacing: 0.1,
            display: 'flex',
        },
        metadataContainer: {
            display: 'flex',
            justifyContent: 'space-between',
        },
    }),
);

const columns: IColumn[] = [
    {
        id: 'timePeriod|timePeriod',
        label: 'Time Period',
        align: 'left',
        maxWidth: 300,
        field: 'startDate',
        sortFields: ['startDate'],
    },
    {
        id: 'hours|hours',
        label: 'Hours',
        align: 'left',
        maxWidth: 300,
        field: 'hours',
        noSort: true,
    },
    {
        id: 'user|user',
        label: 'User',
        align: 'left',
        maxWidth: 300,
        field: 'user.name',
        sortFields: ['user.name'],
    },
    {
        id: 'status|string',
        label: 'Status',
        align: 'left',
        maxWidth: 300,
        field: 'status',
        sortFields: ['status'],
    },
];

const TimeSheetInfiniteList: FunctionComponent<TimeSheetProps> = (props) => {
    const classes = componentStyles();
    const [scrollTop, setScrollTop] = useState<number>(0);

    const {
        timeSheetList,
        skip,
        hasMore,
        onSelectTimeSheetRow,
        setSkip,
        sortBy,
        sortOrder,
        setSortOrder,
        setSortColumn,
    } = props;
    useEffect(() => {
        if (skip === 0) {
            setScrollTop(0);
        }
    }, [skip]);

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    const onClickRow = (id: string, e: any) => {
        onSelectTimeSheetRow(id);
        e.stopPropagation();
    };

    const filteredTimeSheetList = timeSheetList.map((element) => ({
        id: element.id,
        startDate: element.startDate,
        timePeriod: moment(element.startDate).format('L') + ' - ' + moment(element.endDate).format('L'),
        status: element.status,
        hours: round(sum(jsonpath.query(element?.timeCards, '$..timeEntries[*].unitHours')), 2),
        user: element?.user?.name,
    }));

    return (
        <Box className={`${classes.root} verticalFillContainer`}>
            <Box className={`${classes.container} verticalFillContent`}>
                {timeSheetList.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={filteredTimeSheetList}
                        count={filteredTimeSheetList?.length}
                        onClickRow={onClickRow}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        sortOrder={sortOrder}
                        sortBy={sortBy}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                    />
                ) : (
                    <TableEmptyState
                        title="No active Time Sheet."
                        message="All your Time Sheets will be stored here"
                    ></TableEmptyState>
                )}
            </Box>

            {scrollTop > 20 && (
                <Tooltip title="Back to Top" placement="top">
                    <Fab
                        size="small"
                        className={classes.fab}
                        aria-label="edit"
                        onClick={() => {
                            setScrollTop(0);
                        }}
                    >
                        <BackToTopArrow color="primary" />
                    </Fab>
                </Tooltip>
            )}
        </Box>
    );
};

export default TimeSheetInfiniteList;
